import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1554793000-245d3a3c2a51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZyUyMGZhY2FkZXxlbnwwfHx8fDE3ODc4NTUxOTV8MA&ixlib=rb-4.1.0&q=85";

function AuthShell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center px-6 py-12 lg:px-16 relative z-10">
        <div className="w-full max-w-sm fade-up">
          <div className="flex items-center gap-2 mb-10">
            <div className="h-9 w-9 bg-primary flex items-center justify-center">
              <Buildings size={22} weight="bold" className="text-primary-foreground" />
            </div>
            <span className="font-display font-bold text-xl tracking-tighter">LeaseLedger</span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mb-2">{title}</h1>
          <p className="text-muted-foreground text-sm mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO} alt="asset" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#002FA7]/80 via-[#002FA7]/20 to-black/30" />
        <div className="absolute bottom-0 left-0 p-12 text-white z-10">
          <p className="font-display text-3xl font-bold tracking-tight leading-tight max-w-md">
            IFRS 16 compliant lease accounting, done right.
          </p>
          <p className="text-white/70 text-sm mt-3 font-mono">ROU ASSETS · LEASE LIABILITIES · AMORTIZATION</p>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Sign in" subtitle="Access your lease portfolio and IFRS 16 ledger.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            required data-testid="login-email" className="mt-1.5 rounded-sm" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-link">Forgot password?</Link>
          </div>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            required data-testid="login-password" className="mt-1.5 rounded-sm" />
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit"
          className="w-full rounded-sm gap-2 group">
          {loading ? "Signing in..." : "Sign in"} <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
        </Button>
      </form>
      <p className="text-sm text-muted-foreground mt-6">
        No account? <Link to="/register" className="text-primary hover:underline" data-testid="to-register">Create one</Link>
      </p>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Create account" subtitle="Start managing leases with automated IFRS 16 calculations.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label htmlFor="name">Full name</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" className="mt-1.5 rounded-sm" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" className="mt-1.5 rounded-sm" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="register-password" className="mt-1.5 rounded-sm" />
        </div>
        <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-sm">
          {loading ? "Creating..." : "Create account"}
        </Button>
      </form>
      <p className="text-sm text-muted-foreground mt-6">
        Have an account? <Link to="/login" className="text-primary hover:underline" data-testid="to-login">Sign in</Link>
      </p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/auth/forgot-password", { email });
    } catch { /* generic */ }
    setSent(true);
  };
  return (
    <AuthShell title="Reset password" subtitle="We'll email you a secure link to reset your password.">
      {sent ? (
        <div className="space-y-4">
          <p className="text-sm" data-testid="forgot-sent">If that email is registered, a reset link has been sent. Check your inbox.</p>
          <Link to="/login" className="text-primary hover:underline text-sm">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" className="mt-1.5 rounded-sm" />
          </div>
          <Button type="submit" className="w-full rounded-sm" data-testid="forgot-submit">Send reset link</Button>
          <Link to="/login" className="block text-primary hover:underline text-sm">Back to sign in</Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    }
  };
  return (
    <AuthShell title="New password" subtitle="Choose a strong new password for your account.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <Label htmlFor="password">New password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reset-password" className="mt-1.5 rounded-sm" />
        </div>
        <Button type="submit" className="w-full rounded-sm" data-testid="reset-submit">Reset password</Button>
        <Link to="/login" className="block text-primary hover:underline text-sm">Back to sign in</Link>
      </form>
    </AuthShell>
  );
}
